import React, { useState, useEffect, useMemo } from 'react';
import { Lock, Mail, Phone, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService, type UserCredentials } from '@/features/auth/services/authService';
import { notificationService } from '@/services/notificationService';
import { playClickSound } from '@/utils/soundUtils';
import AuthHeader from '@/layouts/components/Header/AuthHeader';
import { AuthMethodLabel, AuthPhoneInput, AuthSectionLabel, PasswordInput } from './AuthFormParts';
import {
    Alert,
    Badge,
    Button,
    Card,
    Field,
    Input,
    PageHeader,
    PageShell,
    SegmentedControl,
    Stepper,
    cx,
    type SegmentItem,
} from '@/components/ui';
import { useTranslation } from 'react-i18next';
import {
    type ForgotChannel,
    getForgotEmailFormatError,
    getForgotPhoneFormatError,
    getForgotNotRegisteredMessage,
    isForgotContactFormatValid,
} from '@/features/auth/utils/forgotPasswordValidation';

/** Cooldown between RESEND taps (forgot-account OTP step); separate from OTP expiry timer */
const FORGOT_RESEND_COOLDOWN_SECONDS = 60;

interface UserAuthenticationProps {
    selectedProvider: {
        id: number | string;
        name: string;
        tagline: string;
        gradient: string;
        icon: string;
    } | null;
    selectedAction: 'deposit' | 'withdrawal' | null;
    onBack: () => void;
    onAuthenticate: (credentials: UserCredentials) => void | Promise<void>;
    onRegister?: () => void;
    totalAmount?: number;
    isWalletFlow?: boolean;
    /** When set (e.g. from PaymentCheckoutPage redirect), navigate here after successful login */
    redirectToAfterLogin?: string;
    hideHeader?: boolean; // Hide header when used in page with layout
    /** Open forgot-password OTP flow on mount (e.g. /login?forgot=1) */
    initialForgotMode?: boolean;
}

type LoginTab = 'username' | 'phone' | 'email';
type ForgotStep = 1 | 2 | 3;

export default function UserAuthentication({
    selectedProvider,
    selectedAction,
    onBack,
    onAuthenticate,
    onRegister,
    totalAmount = 0,
    isWalletFlow = false,
    redirectToAfterLogin,
    hideHeader = false,
    initialForgotMode = false,
}: UserAuthenticationProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState<LoginTab>('username');
    const [isForgotMode, setIsForgotMode] = useState(initialForgotMode);
    const [forgotStep, setForgotStep] = useState<ForgotStep>(1);
    const [forgotChannel, setForgotChannel] = useState<ForgotChannel>('email');
    const [forgotContactTouched, setForgotContactTouched] = useState(false);
    const [forgotContact, setForgotContact] = useState('');
    const [forgotRequestToken, setForgotRequestToken] = useState('');
    const [forgotVerifiedToken, setForgotVerifiedToken] = useState('');
    const [forgotOtp, setForgotOtp] = useState('');
    const [forgotNewPassword, setForgotNewPassword] = useState('');
    const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
    const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
    /** OTP session expiry countdown (matches API "Code expires in") */
    const [forgotOtpCooldown, setForgotOtpCooldown] = useState(300);
    /** Separate from OTP expiry so RESEND is not blocked for the full validity window */
    const [forgotResendCooldown, setForgotResendCooldown] = useState(0);

    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    /** Username+password tab: credentials OK and waiting for the email OTP (2FA). */
    const [username2FA, setUsername2FA] = useState(false);
    const [twoFactorEmail, setTwoFactorEmail] = useState('');
    const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [twoFactorCooldown, setTwoFactorCooldown] = useState(0);
    const [twoFactorSending, setTwoFactorSending] = useState(false);

    // Phone login states
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [phoneCooldown, setPhoneCooldown] = useState(0);
    const [isPhoneCodeSent, setIsPhoneCodeSent] = useState(false);

    // Email login states
    const [email, setEmail] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [emailCooldown, setEmailCooldown] = useState(0);
    const [isEmailCodeSent, setIsEmailCodeSent] = useState(false);

    // Common states
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    /** True while any auth API call is in flight (login, send OTP, forgot-account steps). */
    const apiBusy = isLoading || isSendingCode;

    // Cooldown timers
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (phoneCooldown > 0) {
            interval = setInterval(() => {
                setPhoneCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [phoneCooldown]);

    useEffect(() => {
        if (twoFactorCooldown <= 0) return;
        const timer = setTimeout(() => setTwoFactorCooldown((prev) => prev - 1), 1000);
        return () => clearTimeout(timer);
    }, [twoFactorCooldown]);

    // The code belongs to the credentials that were checked: editing either starts over.
    useEffect(() => {
        setUsername2FA(false);
        setTwoFactorEmail('');
        setTwoFactorMaskedEmail('');
        setTwoFactorCode('');
    }, [username, password]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (emailCooldown > 0) {
            interval = setInterval(() => {
                setEmailCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [emailCooldown]);

    useEffect(() => {
        if (!isForgotMode || forgotStep !== 2 || forgotOtpCooldown <= 0) return;
        const interval = setInterval(() => setForgotOtpCooldown((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [isForgotMode, forgotStep, forgotOtpCooldown]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (forgotResendCooldown > 0) {
            interval = setInterval(() => {
                setForgotResendCooldown((prev) => Math.max(0, prev - 1));
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [forgotResendCooldown]);

    const handleBackClick = () => {
        if (apiBusy) {
            return;
        }
        playClickSound();
        onBack();
    };

    const validatePhoneNumber = (phone: string): boolean => {
        // Use authService validation for international format
        const phoneValidation = authService.validatePhone(phone);
        if (!phoneValidation.isValid) {
            setValidationErrors({ phone: phoneValidation.message });
            return false;
        }
        return true;
    };

    const validateEmail = (emailValue: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setValidationErrors({ email: t('auth.emailInvalid') });
            return false;
        }
        return true;
    };

    const handleSendPhoneCode = async () => {
        if (isSendingCode || phoneCooldown > 0) return;

        setValidationErrors({});
        const phone = phoneNumber.trim();

        if (!phone) {
            setValidationErrors({ phone: t('auth.phoneRequired') });
            return;
        }

        if (!validatePhoneNumber(phone)) {
            return;
        }

        setIsSendingCode(true);
        playClickSound();

        try {
            // Call API to send verification code via Twilio
            const result = await authService.sendPhoneLoginCode(phone);

            if (result.success) {
                setIsPhoneCodeSent(true);
                setPhoneCooldown(60);
                notificationService.success(t('auth.verificationSentPhone'), { duration: 5000 });
            } else {
                setValidationErrors({ general: result.message || t('auth.failedToSendCode') });
                notificationService.error(result.message || t('auth.failedToSendCode'), { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || t('auth.failedToSendVerification');
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleSendEmailCode = async () => {
        if (isSendingCode || emailCooldown > 0) return;

        setValidationErrors({});
        const emailValue = email.trim();

        if (!emailValue) {
            setValidationErrors({ email: t('auth.emailRequired') });
            return;
        }

        if (!validateEmail(emailValue)) {
            return;
        }

        setIsSendingCode(true);
        playClickSound();

        try {
            // Call API to send OTP code via email for email login
            const result = await authService.sendEmailOTP(emailValue);

            if (result.success) {
                setIsEmailCodeSent(true);
                setEmailCooldown(60);
                notificationService.success(t('auth.verificationSentEmail'), { duration: 5000 });
            } else {
                setValidationErrors({ general: result.message || t('auth.failedToSendCode') });
                notificationService.error(result.message || t('auth.failedToSendCode'), { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || t('auth.failedToSendVerification');
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsSendingCode(false);
        }
    };

    const handlePhoneLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (apiBusy) return;

        setValidationErrors({});
        const phone = phoneNumber.trim();
        const code = phoneCode.trim();

        if (!phone) {
            setValidationErrors({ phone: t('auth.phoneRequired') });
            return;
        }

        if (!code) {
            setValidationErrors({ phoneCode: t('auth.verificationCodeRequired') });
            return;
        }

        if (code.length !== 6) {
            setValidationErrors({ phoneCode: t('auth.verificationCodeMustBe6') });
            return;
        }

        setIsLoading(true);
        playClickSound();

        // Actual authentication logic
        try {
            const result = await authService.loginWithPhoneCode(phone, code);

            if (result.success) {

                // Token should be saved by authService.loginWithPhoneCode via apiService.setTokens()
                // Parent (e.g. UserLoginPage) handles redirect via onAuthenticate
                await Promise.resolve(
                    onAuthenticate({
                        phone: phone,
                        phoneCode: code,
                        email: '',
                        password: '',
                        emailCode: ''
                    } as UserCredentials)
                );
            } else {
                // Show error message - if error is a detailed message (contains spaces), show both
                // Otherwise, if error is just a code (like INVALID_CREDENTIALS), only show message
                let errorMessage = result.message || 'Login failed';
                if (result.error && result.error.includes(' ')) {
                    // Error is a detailed message, show both
                    errorMessage = `${errorMessage}\n${result.error}`;
                }
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Login failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (apiBusy) return;

        setValidationErrors({});
        const emailValue = email.trim();
        const code = emailCode.trim();

        if (!emailValue) {
            setValidationErrors({ email: 'Email address is required' });
            return;
        }

        if (!code) {
            setValidationErrors({ emailCode: 'Verification code is required' });
            return;
        }

        if (code.length !== 6) {
            setValidationErrors({ emailCode: 'Verification code must be 6 digits' });
            return;
        }

        setIsLoading(true);
        playClickSound();

        // Actual authentication logic
        try {
            const result = await authService.loginWithEmailCode(emailValue, code);

            if (result.success) {

                // Token should be saved by authService.loginWithEmailCode via apiService.setTokens()
                // Parent (e.g. UserLoginPage) handles redirect via onAuthenticate
                await Promise.resolve(
                    onAuthenticate({
                        email: emailValue,
                        emailCode: code,
                        phone: '',
                        password: '',
                        phoneCode: ''
                    } as UserCredentials)
                );
            } else {
                // Show error message - if error is a detailed message (contains spaces), show both
                // Otherwise, if error is just a code (like INVALID_CREDENTIALS), only show message
                let errorMessage = result.message || 'Login failed';
                if (result.error && result.error.includes(' ')) {
                    // Error is a detailed message, show both
                    errorMessage = `${errorMessage}\n${result.error}`;
                }
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Login failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsLoading(false);
        }
    };


    const resetUsername2FA = () => {
        setUsername2FA(false);
        setTwoFactorEmail('');
        setTwoFactorMaskedEmail('');
        setTwoFactorCode('');
        setTwoFactorCooldown(0);
    };

    /** SegmentedControl plays the click sound when the method changes. */
    const handleTabChange = (tab: LoginTab) => {
        if (apiBusy) return;
        setActiveTab(tab);
        setValidationErrors({});
        resetUsername2FA();
    };

    const notifyTwoFactorCodeSent = (masked: string) => {
        notificationService.success(
            masked
                ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
                : t('auth.verificationSentEmail', 'Verification code sent to your email')
        );
    };

    /** (Re)send the 2FA code to the account's registered email. */
    const sendUsername2FACode = async (target: string) => {
        if (twoFactorSending || twoFactorCooldown > 0) return;
        const id = target.trim();
        if (!id) {
            setValidationErrors({
                general: t(
                    'auth.twoFactorEmailUnavailable',
                    'Could not send a code to your registered email. Please try again or contact support.'
                ),
            });
            return;
        }
        setTwoFactorSending(true);
        try {
            const result = await authService.sendLoginCode(id);
            if (!result.success) {
                setValidationErrors({ general: result.message || t('auth.failedToSendCode', 'Failed to send code') });
                if (result.cooldownSeconds) setTwoFactorCooldown(result.cooldownSeconds);
                return;
            }
            const resolvedEmail = (result.email || (authService.validateEmail(id).isValid ? id : twoFactorEmail) || '').trim();
            const masked =
                result.maskedEmail ||
                (resolvedEmail ? authService.maskEmail(resolvedEmail) : twoFactorMaskedEmail) ||
                '';
            if (resolvedEmail) setTwoFactorEmail(resolvedEmail);
            if (masked) setTwoFactorMaskedEmail(masked);
            setTwoFactorCooldown(result.cooldownSeconds ?? 60);
            notifyTwoFactorCodeSent(masked);
        } catch (error: unknown) {
            setValidationErrors({
                general:
                    (error as { message?: string })?.message ||
                    t('auth.failedToSendVerification', 'Failed to send verification code. Please try again.'),
            });
        } finally {
            setTwoFactorSending(false);
        }
    };

    const handleUsernameLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isLoading) return;
        setValidationErrors({});
        if (!username.trim()) return setValidationErrors({ username: 'Username is required' });
        if (!password.trim()) return setValidationErrors({ password: 'Password is required' });
        const code = twoFactorCode.trim();
        if (username2FA && code.length !== 6) {
            return setValidationErrors({
                general: code
                    ? t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits')
                    : t('auth.verificationCodeRequired', 'Verification code is required'),
            });
        }
        setIsLoading(true);
        playClickSound();
        try {
            if (!username2FA) {
                // Check the password first: an account with 2FA answers with a challenge (and has
                // usually already been emailed a code) instead of a session.
                const probe = await authService.validateCredentialsWithAPI(username.trim(), password);
                if (!probe.success) {
                    const errorMessage = probe.message || 'Login failed';
                    setValidationErrors({ general: errorMessage });
                    notificationService.error(errorMessage, { duration: 10000 });
                    if (probe.cooldownSeconds) setTwoFactorCooldown(probe.cooldownSeconds);
                    return;
                }
                if (probe.two_factor_enabled) {
                    const emailForCode = (probe.email || '').trim();
                    const masked = probe.maskedEmail || (emailForCode ? authService.maskEmail(emailForCode) : '');
                    setUsername2FA(true);
                    setTwoFactorEmail(emailForCode);
                    setTwoFactorMaskedEmail(masked);
                    setTwoFactorCode('');
                    if (probe.codeSent) {
                        setTwoFactorCooldown(probe.cooldownSeconds ?? 60);
                        notifyTwoFactorCodeSent(masked);
                    } else {
                        setTwoFactorCooldown(0);
                        await sendUsername2FACode(emailForCode || username.trim());
                    }
                    return;
                }
                // No 2FA: the probe already stored the session.
                await Promise.resolve(
                    onAuthenticate({ username: username.trim(), email: '', password, emailCode: '', phone: '', phoneCode: '' } as UserCredentials)
                );
                return;
            }

            const result = await authService.authenticateUser(
                {
                    username: username.trim(),
                    email: twoFactorEmail || '',
                    password,
                    emailCode: code,
                    phone: '',
                    phoneCode: '',
                } as UserCredentials,
                true
            );
            if (!result.success) {
                let errorMessage = result.message || 'Login failed';
                if (result.error && result.error.includes(' ')) {
                    errorMessage = `${errorMessage}\n${result.error}`;
                }
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
                return;
            }
            await Promise.resolve(
                onAuthenticate({ username: username.trim(), email: '', password, emailCode: '', phone: '', phoneCode: '' } as UserCredentials)
            );
        } catch (error: unknown) {
            const errorMessage =
                (error as { message?: string })?.message || 'Login failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsLoading(false);
        }
    };

    const goToForgot = () => {
        if (apiBusy) return;
        const channel: ForgotChannel =
            activeTab === 'phone' ? 'phone' : activeTab === 'email' ? 'email' : 'email';
        setIsForgotMode(true);
        setForgotStep(1);
        setForgotChannel(channel);
        setForgotContactTouched(false);
        setForgotOtp('');
        setForgotRequestToken('');
        setForgotVerifiedToken('');
        setForgotNewPassword('');
        setForgotConfirmPassword('');
        setForgotOtpCooldown(300);
        setForgotResendCooldown(0);
        setValidationErrors({});
        if (channel === 'phone' && phoneNumber.trim()) {
            setForgotContact(phoneNumber.trim());
        } else if (channel === 'email' && email.trim()) {
            setForgotContact(email.trim());
        } else {
            setForgotContact('');
        }
    };

    const forgotContactFormatError = useMemo(() => {
        const formatError =
            forgotChannel === 'email'
                ? getForgotEmailFormatError(forgotContact)
                : getForgotPhoneFormatError(forgotContact);
        if (!formatError) return null;
        if (forgotContactTouched || forgotContact.trim()) return formatError;
        return null;
    }, [forgotChannel, forgotContact, forgotContactTouched]);

    const forgotContactDisplayError = validationErrors.forgotContact || forgotContactFormatError;

    const canSendForgotCode =
        isForgotMode &&
        forgotStep === 1 &&
        isForgotContactFormatValid(forgotChannel, forgotContact) &&
        !apiBusy;

    const handleForgotRequest = async () => {
        const contact = forgotContact.trim();
        setForgotContactTouched(true);
        const formatError =
            forgotChannel === 'email'
                ? getForgotEmailFormatError(contact)
                : getForgotPhoneFormatError(contact);
        if (formatError) {
            setValidationErrors((prev) => ({ ...prev, forgotContact: formatError }));
            return;
        }

        setIsLoading(true);
        setValidationErrors((prev) => {
            const next = { ...prev };
            delete next.forgotContact;
            delete next.general;
            return next;
        });
        try {
            const result = await import('@/features/auth/services/passwordResetService').then((m) =>
                m.passwordResetService.requestResetByOtp(forgotChannel, contact)
            );
            const token = result.data?.token;

            if (result.success && token) {
                setForgotRequestToken(token);
                setForgotStep(2);
                const exp = (result.data as { expiresInSeconds?: number } | undefined)?.expiresInSeconds;
                setForgotOtpCooldown(typeof exp === 'number' && exp > 0 ? exp : 600);
                const nextResend = (result.data as { next_resend_at?: string } | undefined)?.next_resend_at;
                if (nextResend) {
                    const remaining = Math.ceil((new Date(nextResend).getTime() - Date.now()) / 1000);
                    setForgotResendCooldown(remaining > 0 ? remaining : FORGOT_RESEND_COOLDOWN_SECONDS);
                } else {
                    setForgotResendCooldown(FORGOT_RESEND_COOLDOWN_SECONDS);
                }
                setForgotOtp('');
                notificationService.success(
                    forgotStep === 2 ? 'A new verification code has been sent' : 'Verification code sent'
                );
                return;
            }

            if (result.success && !token) {
                const msg = result.message || getForgotNotRegisteredMessage(forgotChannel);
                setValidationErrors({ general: msg });
                notificationService.info(msg, { duration: 6000 });
                return;
            }

            const apiFormatError =
                result.statusCode === 422 &&
                (result.message?.includes('email') || result.message?.includes('phone'));
            if (apiFormatError) {
                setValidationErrors({ forgotContact: result.message });
                return;
            }

            setValidationErrors({ general: result.message || 'Failed to request OTP' });
            notificationService.error(result.message || 'Failed to request OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotVerifyOtp = async () => {
        if (forgotOtp.trim().length !== 6) {
            setValidationErrors({ forgotOtp: 'OTP must be 6 digits' });
            return;
        }
        setIsLoading(true);
        setValidationErrors({});
        try {
            const result = await import('@/features/auth/services/passwordResetService').then((m) =>
                m.passwordResetService.verifyResetOtp(forgotRequestToken, forgotOtp.trim())
            );
            const verifiedToken = result.data?.token;
            if (!result.success || !verifiedToken) {
                const msg = result.message || 'Invalid or expired OTP.';
                setValidationErrors({ general: msg, forgotOtp: msg });
                notificationService.error(msg, { duration: 8000 });
                return;
            }
            setForgotVerifiedToken(verifiedToken);
            setForgotStep(3);
            notificationService.success('OTP verified. Choose a new password.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotSetPassword = async () => {
        if (!forgotNewPassword.trim()) {
            setValidationErrors({ forgotNewPassword: 'New password is required' });
            return;
        }
        if (forgotNewPassword !== forgotConfirmPassword) {
            setValidationErrors({ forgotConfirmPassword: 'Passwords do not match' });
            return;
        }
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(forgotNewPassword)) {
            setValidationErrors({
                forgotNewPassword:
                    'Password must be at least 8 characters with uppercase, lowercase, number, and special character.',
            });
            return;
        }
        setIsLoading(true);
        setValidationErrors({});
        try {
            const result = await import('@/features/auth/services/passwordResetService').then((m) =>
                m.passwordResetService.confirmResetByOtp(forgotVerifiedToken, forgotNewPassword)
            );
            if (!result.success) {
                const msg = result.message || 'Failed to reset password.';
                setValidationErrors({ general: msg });
                notificationService.error(msg, { duration: 8000 });
                return;
            }
            notificationService.success(result.message || 'Password reset successfully. You can sign in now.');
            setIsForgotMode(false);
            setForgotStep(1);
            setActiveTab('username');
        } finally {
            setIsLoading(false);
        }
    };

    const actionText = selectedAction === 'deposit' ? 'Deposit' : selectedAction === 'withdrawal' ? 'Withdrawal' : 'Login';
    /** Money in = primary (magenta); withdrawal keeps its blue as info. */
    const actionTone = selectedAction === 'withdrawal' ? 'info' : 'primary';

    // Determine title and subtitle for AuthHeader
    const headerTitle = isForgotMode
        ? t('authHeader.forgotPassword')
        : selectedProvider
          ? selectedProvider.name.toUpperCase()
          : t('authHeader.userLogin');
    const headerSubtitle = isForgotMode
        ? t('authHeader.forgotSubtitle')
        : selectedAction
          ? `Please verify your identity to ${actionText.toLowerCase()}`
          : t('authHeader.verifyIdentitySubtitle');

    const methodItems: SegmentItem<LoginTab>[] = [
        { value: 'username', label: <AuthMethodLabel icon={<UserIcon aria-hidden />} text="Username" /> },
        { value: 'phone', label: <AuthMethodLabel icon={<Phone aria-hidden />} text="Phone" /> },
        { value: 'email', label: <AuthMethodLabel icon={<Mail aria-hidden />} text="Email" /> },
    ];

    const forgotChannelItems: SegmentItem<ForgotChannel>[] = [
        { value: 'email', label: <AuthMethodLabel icon={<Mail aria-hidden />} text="Email" /> },
        { value: 'phone', label: <AuthMethodLabel icon={<Phone aria-hidden />} text="Phone" /> },
    ];

    return (
        <div className={cx('flex min-w-0 flex-col', !hideHeader && 'min-h-screen')}>
            {!hideHeader && (
                <AuthHeader
                    title={headerTitle}
                    subtitle={headerSubtitle}
                />
            )}

            <PageShell width="narrow">
                {/* Phones: page title here. From md up the auth header bar shows it. */}
                <PageHeader
                    className="@bp-md:hidden"
                    title={isForgotMode ? t('authHeader.forgotPassword') : t('authHeader.userLogin')}
                    subtitle={isForgotMode ? t('authHeader.forgotSubtitle') : t('authHeader.verifyIdentitySubtitle')}
                />

                {selectedAction && (
                    <div className="flex items-center justify-center gap-1.5 font-ui text-small text-secondary">
                        <span>Action:</span>
                        <Badge tone={actionTone} dot={false}>
                            {actionText}
                        </Badge>
                    </div>
                )}

                {!isWalletFlow && totalAmount > 0 && (
                    <Alert tone="info">
                        You're about to deposit <span className="font-semibold text-primary">${Math.floor(totalAmount)}</span> to the games
                    </Alert>
                )}

                {/* Method selector — dimmed while the forgot-account flow is open */}
                <div
                    className={cx('transition-opacity', (isForgotMode || apiBusy) && 'pointer-events-none opacity-45')}
                    aria-hidden={isForgotMode}
                >
                    <SegmentedControl<LoginTab>
                        aria-label={t('auth.methodLabel')}
                        items={methodItems}
                        value={activeTab}
                        onChange={handleTabChange}
                        disabled={apiBusy}
                    />
                </div>

                <Card title={isForgotMode ? t('authHeader.forgotPassword') : undefined}>
                    {isForgotMode ? (
                        <div className="flex w-full flex-col gap-4">
                            <Stepper
                                steps={['1 Contact', '2 OTP', '3 Password']}
                                current={forgotStep - 1}
                                aria-label={t('auth.forgotStepsLabel')}
                            />
                            {forgotStep === 1 && (
                                <p className="m-0 font-ui text-small text-secondary">
                                    {forgotChannel === 'email'
                                        ? 'Enter the email address registered on your account.'
                                        : 'Enter the phone number registered on your account.'}
                                </p>
                            )}
                            {validationErrors.general && <Alert tone="danger">{validationErrors.general}</Alert>}
                            {forgotStep === 1 && (
                                <div className="flex flex-col gap-4">
                                    <SegmentedControl<ForgotChannel>
                                        aria-label={t('auth.forgotChannelLabel')}
                                        items={forgotChannelItems}
                                        value={forgotChannel}
                                        disabled={apiBusy}
                                        onChange={(channel) => {
                                            setForgotChannel(channel);
                                            setForgotContactTouched(false);
                                        }}
                                    />
                                    <Field
                                        label={forgotChannel === 'email' ? 'Email address' : 'Phone number'}
                                        error={forgotContactDisplayError || undefined}
                                    >
                                        {forgotChannel === 'phone' ? (
                                            <AuthPhoneInput
                                                id="forgot-contact"
                                                international
                                                placeholder="Enter your phone number"
                                                value={forgotContact}
                                                onChange={setForgotContact}
                                                onBlur={() => setForgotContactTouched(true)}
                                            />
                                        ) : (
                                            <Input
                                                id="forgot-contact"
                                                type="email"
                                                autoComplete="email"
                                                placeholder="name@example.com"
                                                leading={<Mail />}
                                                value={forgotContact}
                                                onChange={(e) => setForgotContact(e.target.value)}
                                                onBlur={() => setForgotContactTouched(true)}
                                            />
                                        )}
                                    </Field>
                                    <div className="flex flex-col-reverse gap-2 @bp-sm:flex-row">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            disabled={isLoading}
                                            onClick={() => {
                                                setIsForgotMode(false);
                                                setForgotContactTouched(false);
                                            }}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="primary"
                                            disabled={!canSendForgotCode}
                                            loading={isLoading}
                                            onClick={handleForgotRequest}
                                            className="flex-1"
                                        >
                                            {isLoading ? 'Sending...' : 'SEND CODE'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {forgotStep === 2 && (
                                <div className="flex flex-col gap-4">
                                    <p className="m-0 font-ui text-small text-tertiary">
                                        Code expires in{' '}
                                        <span className="tabular-nums text-secondary">
                                            {Math.floor(forgotOtpCooldown / 60)}:{String(forgotOtpCooldown % 60).padStart(2, '0')}
                                        </span>
                                    </p>
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center gap-2">
                                            <Input
                                                containerClassName="min-w-0 flex-1"
                                                value={forgotOtp}
                                                onChange={(e) => {
                                                    setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                    if (validationErrors.forgotOtp || validationErrors.general) {
                                                        setValidationErrors((prev) => {
                                                            const next = { ...prev };
                                                            delete next.forgotOtp;
                                                            delete next.general;
                                                            return next;
                                                        });
                                                    }
                                                }}
                                                placeholder="000000"
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                aria-label={t('auth.verificationCode', 'Verification code')}
                                                invalid={!!validationErrors.forgotOtp}
                                                aria-describedby={validationErrors.forgotOtp ? 'forgot-otp-error' : undefined}
                                            />
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                disabled={forgotResendCooldown > 0 || isLoading}
                                                loading={isLoading}
                                                onClick={handleForgotRequest}
                                                className="min-w-24"
                                            >
                                                {isLoading
                                                    ? null
                                                    : forgotResendCooldown > 0
                                                      ? `RESEND (${forgotResendCooldown}s)`
                                                      : 'RESEND'}
                                            </Button>
                                        </div>
                                        {validationErrors.forgotOtp && (
                                            <p id="forgot-otp-error" className="m-0 font-ui text-small text-danger" role="alert">
                                                {validationErrors.forgotOtp}
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        fullWidth
                                        disabled={isLoading}
                                        loading={isLoading}
                                        onClick={() => void handleForgotVerifyOtp()}
                                    >
                                        {isLoading ? 'Working...' : 'VERIFY CODE'}
                                    </Button>
                                </div>
                            )}
                            {forgotStep === 3 && (
                                <div className="flex flex-col gap-4">
                                    <Field label="New password" error={validationErrors.forgotNewPassword}>
                                        <PasswordInput
                                            id="forgot-new-password"
                                            show={showForgotNewPassword}
                                            onToggleShow={() => setShowForgotNewPassword((v) => !v)}
                                            silentToggle
                                            value={forgotNewPassword}
                                            onChange={(e) => setForgotNewPassword(e.target.value)}
                                            autoComplete="new-password"
                                        />
                                    </Field>
                                    <Field label="Confirm password" error={validationErrors.forgotConfirmPassword}>
                                        <Input
                                            id="forgot-confirm-password"
                                            type={showForgotNewPassword ? 'text' : 'password'}
                                            leading={<Lock />}
                                            value={forgotConfirmPassword}
                                            onChange={(e) => setForgotConfirmPassword(e.target.value)}
                                            autoComplete="new-password"
                                        />
                                    </Field>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        fullWidth
                                        disabled={isLoading}
                                        loading={isLoading}
                                        onClick={() => void handleForgotSetPassword()}
                                    >
                                        {isLoading ? 'Saving...' : 'RESET PASSWORD'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {activeTab === 'username' && (
                                <form onSubmit={handleUsernameLogin} className="flex w-full flex-col gap-4">
                                    <Field label="Username" error={validationErrors.username}>
                                        <Input
                                            id="login-username"
                                            leading={<UserIcon />}
                                            value={username}
                                            onChange={(e) => {
                                                setUsername(e.target.value);
                                                setValidationErrors((prev) => {
                                                    if (!prev.username && !prev.general) return prev;
                                                    const next = { ...prev };
                                                    delete next.username;
                                                    delete next.general;
                                                    return next;
                                                });
                                            }}
                                            autoComplete="username"
                                            placeholder="Enter your username"
                                        />
                                    </Field>
                                    <Field label="Password" error={validationErrors.password}>
                                        <PasswordInput
                                            id="login-password"
                                            show={showPassword}
                                            onToggleShow={() => setShowPassword((v) => !v)}
                                            silentToggle
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setValidationErrors((prev) => {
                                                    if (!prev.password && !prev.general) return prev;
                                                    const next = { ...prev };
                                                    delete next.password;
                                                    delete next.general;
                                                    return next;
                                                });
                                            }}
                                            autoComplete="current-password"
                                            placeholder="Enter your password"
                                        />
                                    </Field>
                                    {username2FA && (
                                        <div data-testid="login-2fa-step">
                                            <Field
                                                label={
                                                    twoFactorMaskedEmail
                                                        ? t('auth.twoFactorHint', 'Enter the 6-digit code sent to {{email}}', {
                                                              email: twoFactorMaskedEmail,
                                                          })
                                                        : t(
                                                              'auth.twoFactorHintGeneric',
                                                              'Enter the 6-digit code sent to your registered email'
                                                          )
                                                }
                                                action={
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        className="min-w-24"
                                                        disabled={twoFactorSending || twoFactorCooldown > 0 || isLoading}
                                                        onClick={() => {
                                                            playClickSound();
                                                            void sendUsername2FACode(twoFactorEmail.trim() || username.trim());
                                                        }}
                                                    >
                                                        {twoFactorCooldown > 0
                                                            ? `${twoFactorCooldown}s`
                                                            : t('auth.resendCode', 'Resend code')}
                                                    </Button>
                                                }
                                            >
                                                <Input
                                                    id="login-2fa-code"
                                                    value={twoFactorCode}
                                                    onChange={(e) => {
                                                        setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                        setValidationErrors((prev) => {
                                                            if (!prev.general) return prev;
                                                            const next = { ...prev };
                                                            delete next.general;
                                                            return next;
                                                        });
                                                    }}
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                    autoFocus
                                                    aria-label={t('auth.verificationCode', 'Verification code')}
                                                    placeholder="000000"
                                                />
                                            </Field>
                                        </div>
                                    )}
                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={goToForgot}
                                            disabled={apiBusy}
                                        >
                                            Forgot password?
                                        </Button>
                                    </div>
                                    {validationErrors.general && (
                                        <Alert tone="danger">
                                            <span className="whitespace-pre-line">{validationErrors.general}</span>
                                        </Alert>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        fullWidth
                                        disabled={isLoading}
                                        loading={isLoading}
                                    >
                                        {isLoading
                                            ? 'Logging in...'
                                            : username2FA
                                              ? t('auth.verifyAndSignIn', 'VERIFY & LOG IN')
                                              : 'LOGIN'}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'phone' && (
                                <form onSubmit={handlePhoneLogin} className="flex w-full flex-col gap-4">
                                    <AuthSectionLabel icon={<Phone aria-hidden />} text="Phone Login" />
                                    <Field
                                        label="Phone Number"
                                        error={validationErrors.phone}
                                        hint="Select country and enter your phone number"
                                    >
                                        <AuthPhoneInput
                                            id="phone"
                                            placeholder="Enter your phone number"
                                            value={phoneNumber}
                                            onChange={(v) => {
                                                setPhoneNumber(v);
                                                setValidationErrors((prev) => {
                                                    if (!prev.phone) return prev;
                                                    const { phone: _phone, ...rest } = prev;
                                                    return rest;
                                                });
                                            }}
                                            numberInputProps={{ required: true }}
                                        />
                                    </Field>
                                    <Field
                                        label="Verification Code"
                                        error={validationErrors.phoneCode}
                                        action={
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="min-w-24"
                                                onClick={handleSendPhoneCode}
                                                disabled={isSendingCode || phoneCooldown > 0 || !phoneNumber.trim() || isLoading}
                                                loading={isSendingCode}
                                            >
                                                {isSendingCode ? null : phoneCooldown > 0 ? `${phoneCooldown}s` : 'GET CODE'}
                                            </Button>
                                        }
                                    >
                                        <Input
                                            id="phoneCode"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            maxLength={6}
                                            value={phoneCode}
                                            onChange={(e) => { setPhoneCode(e.target.value.replace(/\D/g, '')); setValidationErrors({}); }}
                                            placeholder="000000"
                                            required
                                        />
                                    </Field>

                                    {validationErrors.general && <Alert tone="danger">{validationErrors.general}</Alert>}

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        fullWidth
                                        disabled={isLoading || isSendingCode || !phoneCode.trim() || phoneCode.length !== 6}
                                        loading={isLoading}
                                    >
                                        {isLoading ? 'Logging in...' : 'LOGIN'}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'email' && (
                                <form onSubmit={handleEmailLogin} className="flex w-full flex-col gap-4">
                                    <AuthSectionLabel icon={<Mail aria-hidden />} text="Email Login" />
                                    <Field label="Email Address" error={validationErrors.email}>
                                        <Input
                                            id="email"
                                            type="email"
                                            leading={<Mail />}
                                            value={email}
                                            onChange={(e) => { setEmail(e.target.value); setValidationErrors({}); }}
                                            placeholder="Enter your email address"
                                            required
                                        />
                                    </Field>
                                    <Field
                                        label="Verification Code"
                                        error={validationErrors.emailCode}
                                        action={
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="min-w-24"
                                                onClick={handleSendEmailCode}
                                                disabled={isSendingCode || emailCooldown > 0 || !email.trim() || isLoading}
                                                loading={isSendingCode}
                                            >
                                                {isSendingCode ? null : emailCooldown > 0 ? `${emailCooldown}s` : 'GET CODE'}
                                            </Button>
                                        }
                                    >
                                        <Input
                                            id="emailCode"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            maxLength={6}
                                            value={emailCode}
                                            onChange={(e) => { setEmailCode(e.target.value.replace(/\D/g, '')); setValidationErrors({}); }}
                                            placeholder="000000"
                                            required
                                        />
                                    </Field>

                                    {validationErrors.general && <Alert tone="danger">{validationErrors.general}</Alert>}

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        fullWidth
                                        disabled={isLoading || isSendingCode || !emailCode.trim() || emailCode.length !== 6}
                                        loading={isLoading}
                                    >
                                        {isLoading ? 'Logging in...' : 'LOGIN'}
                                    </Button>
                                </form>
                            )}
                        </>
                    )}

                    <Alert tone="success" title="Secure connection established">
                        Your verification code is encrypted in transit. We never sell your personal information.
                    </Alert>
                </Card>
            </PageShell>
        </div>
    );
}
